import React, { useState, useRef, useLayoutEffect, useImperativeHandle } from "react";
import { v4 as uuid } from "uuid";
import { Grid } from "@mui/material";
import { TextField, CheckboxField, Alert } from "@curves/ui-base";
import { ReadTextField } from "@curves/ui-shared";
import { PaidList } from "@icoach/documents/components/";
import { SignBox } from "@icoach/components/";
import { displayDateText } from "@util/moment";
import { getRadioFieldResult, getErrorModuleStatus } from "@util/utils";
import { resolveInitialPaymentAmount } from "@icoach/documents/membership/transferIn/transferInFormRules";
import { useMembershipContext } from "@icoach/documents/membership/MembershipContext";

const FeeItem = React.forwardRef((props, ref) => {
	const { temporarySourceDataRef, isReadOnly = false } = props;
	const {
		expectedFirstMonthFee: expectedFirstMonthFeeProps,
		cashPaymentAmount: cashPaymentAmountProps,
		cardPaymentAmount: cardPaymentAmountProps,
		isImmediatelyIssuing: isImmediatelyIssuingProps = true,
	} = temporarySourceDataRef.current;

	const [expectedFirstMonthFee, setExpectedFirstMonthFee] = useState(expectedFirstMonthFeeProps);
	const [isImmediatelyIssuing, setIsImmediatelyIssuing] = useState(isImmediatelyIssuingProps);
	// 如果初始未勾選，金額應為 0
	const initialCashAmount = resolveInitialPaymentAmount(isImmediatelyIssuingProps, cashPaymentAmountProps);
	const initialCardAmount = resolveInitialPaymentAmount(isImmediatelyIssuingProps, cardPaymentAmountProps);
	const [cashPaymentAmount, setCashPaymentAmount] = useState(initialCashAmount);
	const [cardPaymentAmount, setCardPaymentAmount] = useState(initialCardAmount);
	const paymentAmountRef = useRef(null);
	const immediatelyIssuingRef = useRef(null);
	const total = expectedFirstMonthFee;
	const isPaymentReadOnly = !isImmediatelyIssuing || isReadOnly;

	const handleFirstMonthFeeOnChange = e => {
		setExpectedFirstMonthFee(e.target.value);
	};

	const handleImmediatelyIssuingChange = e => {
		const checked = e.target.checked;
		setIsImmediatelyIssuing(checked);
		if (!checked) {
			setCashPaymentAmount(0);
			setCardPaymentAmount(0);
		}
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				const immChecked = immediatelyIssuingRef.current.getResult();
				const params = {
					expectedFirstMonthFee,
					cashPaymentAmount: Number(cashPaymentAmount) || 0,
					cardPaymentAmount: Number(cardPaymentAmount) || 0,
					isImmediatelyIssuing: getRadioFieldResult(immChecked, "checked", false),
				};
				return params;
			},
		}),
		// eslint-disable-next-line
		[expectedFirstMonthFee, cashPaymentAmount, cardPaymentAmount],
	);

	return (
		<React.Fragment>
			<PaidList
				ref={paymentAmountRef}
				title="初次費用"
				cashPaymentAmountProps={{
					key: cashPaymentAmount,
					value: cashPaymentAmount,
					readOnly: isPaymentReadOnly,
					onChangeNative: e => setCashPaymentAmount(e.target.value),
					min: 0,
				}}
				cardPaymentAmountProps={{
					key: cardPaymentAmount,
					value: cardPaymentAmount,
					readOnly: isPaymentReadOnly,
					onChangeNative: e => setCardPaymentAmount(e.target.value),
					min: 0,
				}}
				readOnly={isReadOnly}
				className="mb-3"
				total={total}
			>
				<PaidList.PaidListItem label="當月月費" required>
					<TextField maskType={"MONEY"} value={expectedFirstMonthFee} onChange={handleFirstMonthFeeOnChange} inputProps={{ placeholder: 0, min: 0 }} readOnly={isReadOnly} required />
				</PaidList.PaidListItem>
			</PaidList>
			<CheckboxField ref={immediatelyIssuingRef} className={"mb-1"} defaultChecked={[isImmediatelyIssuing]} onChange={handleImmediatelyIssuingChange}>
				<CheckboxField.Checkbox label={"是否收款並直接開立發票"} value={true} />
			</CheckboxField>
			<Alert className={"alert-justify-content-start"} severity={"info"} variant={"filled"} color={"info"}>
				若未直接開立，需到 新增收入 確認結帳方可確認收款及開立
			</Alert>
		</React.Fragment>
	);
});

// 初次費用
const MembershipTransferInFee = React.forwardRef((props, ref) => {
	const { temporarySourceDataRef, errorModule, isReadOnly } = useMembershipContext();
	const [refreshKey, setRefreshKey] = useState(null);
	const { contractStartDate, contractEndDate, memberShipStartDate, memberShipEndDate, firstCheckContract, firstSign } = temporarySourceDataRef.current;
	const feeItemRef = useRef(null);
	const firstCheckContractRef = useRef(null);
	const firstSignRef = useRef(null);

	const setParams = () => {
		if (isReadOnly) return false;
		const { expectedFirstMonthFee, cashPaymentAmount, cardPaymentAmount, isImmediatelyIssuing } = feeItemRef.current.getResult();
		const isConfirm = (firstCheckContractRef.current.getResult() || []).find(({ checked }) => checked);

		temporarySourceDataRef.current.expectedFirstMonthFee = expectedFirstMonthFee;
		temporarySourceDataRef.current.cashPaymentAmount = cashPaymentAmount;
		temporarySourceDataRef.current.cardPaymentAmount = cardPaymentAmount;
		temporarySourceDataRef.current.isImmediatelyIssuing = isImmediatelyIssuing;
		temporarySourceDataRef.current.firstCheckContract = isConfirm ? isConfirm.checked : false;
		temporarySourceDataRef.current.firstSign = firstSignRef.current.getResult();
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => setParams(),
		}),
		// eslint-disable-next-line
		[],
	);

	useLayoutEffect(
		() => {
			setRefreshKey(uuid());
			return () => setParams();
		},
		// eslint-disable-next-line
		[],
	);

	return (
		<React.Fragment key={refreshKey}>
			<Grid spacing={3} container>
				<Grid xs={6} item>
					<FeeItem ref={feeItemRef} temporarySourceDataRef={temporarySourceDataRef} isReadOnly={isReadOnly} />
				</Grid>
				<Grid xs={6} item>
					<Grid container spacing={3}>
						<Grid xs={12} item>
							<ReadTextField label="合約期間">{displayDateText(contractStartDate, contractEndDate)}</ReadTextField>
						</Grid>
						<Grid xs={12} item>
							<ReadTextField label="會籍期間">{displayDateText(memberShipStartDate, memberShipEndDate)}</ReadTextField>
						</Grid>
						<Grid xs={12} item>
							<CheckboxField ref={firstCheckContractRef} defaultChecked={[firstCheckContract]} readOnly={isReadOnly} required>
								<CheckboxField.Checkbox label="本人已了解會籍內容，並確認使用。" />
							</CheckboxField>
						</Grid>
						{!isReadOnly && (
							<Grid xs={12} item>
								<SignBox key={uuid()} ref={firstSignRef} className="m-0" defaultValue={firstSign} title="會員簽名" {...getErrorModuleStatus(errorModule, "firstSign")} />
							</Grid>
						)}
					</Grid>
				</Grid>
			</Grid>
		</React.Fragment>
	);
});

export default MembershipTransferInFee;
